import { Vector3, Mesh, Scene, StandardMaterial, Vector4, AbstractMesh } from '@babylonjs/core';
import { TBalusterAdjust, TDimension2D, TPlaneContext } from '../helpers/type';
import {
	dataDrawWallSidePorch,
	dataDrawWallFrontPorch,
	dataDrawWallMiddlePorch,
	dataDrawWallLShapePorch,
	dataDrawWallCornerPorch,
	dataCornerTrimFrontPorch,
	dataCornerTrimSidePorch,
	dataCornerTrimMiddlePorch,
	dataCornerTrimCornerPorch,
	dataCornerTrimLShapePorch,
	mappingSide,
	mappingSideRevert,
	switchPorchConfig,
	sidesPorchConfig,
	skeletonMapping,
	mainSidePlaceDoor,
	swapComponentSideMapping,
} from './config';
import * as helpers from '../helpers';
import {
	SWAP_SIDE_DELUXE_PORCH,
	SWAP_SIDE_L_SHAPE_PORCH,
	SWAP_SIDE_CORNER_PORCH,
	PORCH_PRESET,
} from '../ssb_constants';

/**
 * Adjusts baluster start and end points based on the side and applies offset adjustments.
 * Ensures points are ordered correctly (start < end) for each side axis, then applies
 * side-specific offsets (X and Z) and vertical offset (railOffsetFloor + railMeshHeight / 2).
 *
 * @param {TBalusterAdjust} obj - Configuration object containing:
 *   - side: 'left' | 'right' | 'front' | 'back'
 *   - startPoint: Vector3 - Initial start point
 *   - endPoint: Vector3 - Initial end point
 *   - offsetX: number - X-axis offset
 *   - offsetZ: number - Z-axis offset
 *   - railOffsetFloor: number - Floor offset for rail
 *   - railMeshHeight: number - Height of rail mesh
 * @returns {Object} Object with adjusted startPoint and endPoint (Vector3)
 */
export const adjustBalusterPoints = (obj: TBalusterAdjust) => {
	const { side, startPoint, endPoint, offsetX, offsetZ, railOffsetFloor, railMeshHeight } = obj;
	let adjustedStart = startPoint.clone();
	let adjustedEnd = endPoint.clone();

	if (side === 'front' || side === 'back') {
		if (startPoint.x > endPoint.x) {
			adjustedStart = endPoint.clone();
			adjustedEnd = startPoint.clone();
		}
	} else {
		if (startPoint.z > endPoint.z) {
			adjustedStart = endPoint.clone();
			adjustedEnd = startPoint.clone();
		}
	}
	if (side === 'left') {
		adjustedStart = adjustedStart.add(
			new Vector3(offsetX, railOffsetFloor + railMeshHeight / 2, offsetZ),
		);
		adjustedEnd = adjustedEnd.add(
			new Vector3(offsetX, railOffsetFloor + railMeshHeight / 2, -offsetZ),
		);
	} else if (side === 'right') {
		adjustedStart = adjustedStart.add(
			new Vector3(-offsetX, railOffsetFloor + railMeshHeight / 2, offsetZ),
		);
		adjustedEnd = adjustedEnd.add(
			new Vector3(-offsetX, railOffsetFloor + railMeshHeight / 2, -offsetZ),
		);
	} else if (side === 'front') {
		adjustedStart = adjustedStart.add(
			new Vector3(offsetZ, railOffsetFloor + railMeshHeight / 2, -offsetX),
		);
		adjustedEnd = adjustedEnd.add(
			new Vector3(-offsetZ, railOffsetFloor + railMeshHeight / 2, -offsetX),
		);
	} else if (side === 'back') {
		adjustedStart = adjustedStart.add(
			new Vector3(offsetZ, railOffsetFloor + railMeshHeight / 2, offsetX),
		);
		adjustedEnd = adjustedEnd.add(
			new Vector3(-offsetZ, railOffsetFloor + railMeshHeight / 2, offsetX),
		);
	}
	return { startPoint: adjustedStart, endPoint: adjustedEnd };
};

/**
 * @param {Object} params
 * @returns {Object} { left: { position, rotation }, right: { position, rotation } }
 */
export const calculateBraceTransform = ({
	currentColumnMesh,
	pointTop,
	adjustX,
	adjustZ,
	side,
	isOutermost,
	braceRotate,
}) => {
	const { x: colX, z: colZ } = currentColumnMesh.position;
	const posY = pointTop.y;
	let positionLeft = new Vector3(colX + adjustX, posY, colZ + adjustZ);
	let positionRight = new Vector3(colX - adjustX, posY, colZ - adjustZ);

	const isFrontBack = side === 'front' || side === 'back';

	let rotationLeft = isFrontBack ? new Vector3(0, Math.PI, 0) : new Vector3(0, Math.PI / 2, 0);

	let rotationRight = isFrontBack ? new Vector3(0, 0, 0) : new Vector3(0, -Math.PI / 2, 0);

	if (isOutermost) {
		if (braceRotate === 'left') {
			if (side === 'right') {
				rotationLeft = new Vector3(0, 0, 0);
			} else if (side === 'back') {
				rotationLeft = new Vector3(0, Math.PI / 2, 0);
			} else {
				rotationLeft = rotationLeft.add(new Vector3(0, Math.PI / 2, 0));
			}
		} else if (braceRotate === 'right') {
			if (side === 'right') {
				rotationRight = new Vector3(0, 0, 0);
			} else if (side === 'back') {
				rotationRight = new Vector3(0, Math.PI / 2, 0);
			} else {
				rotationRight = rotationRight.add(new Vector3(0, -Math.PI / 2, 0));
			}
		}

		if (isFrontBack) {
			const adjustXbraceRotate = side === 'front' ? -adjustX : adjustX;
			if (braceRotate === 'right') {
				positionRight = new Vector3(colX - adjustX, posY, colZ + adjustXbraceRotate);
			}
			if (braceRotate === 'left') {
				positionLeft = new Vector3(colX + adjustX, posY, colZ + adjustXbraceRotate);
			}
		} else {
			const adjustZbraceRotate = side === 'left' ? adjustZ : -adjustZ;
			if (braceRotate === 'right') {
				positionRight = new Vector3(colX + adjustZbraceRotate, posY, colZ - adjustZ);
			}
			if (braceRotate === 'left') {
				positionLeft = new Vector3(colX + adjustZbraceRotate, posY, colZ + adjustZ);
			}
		}

		if (braceRotate === 'left') {
			positionLeft = positionLeft.add(new Vector3(-adjustX, 0, -adjustZ));
		}
		if (braceRotate === 'right') {
			positionRight = positionRight.add(new Vector3(adjustX, 0, adjustZ));
		}
	}

	return {
		left: { position: positionLeft, rotation: rotationLeft },
		right: { position: positionRight, rotation: rotationRight },
	};
};

export const getPointsBetween = (
	startPoint: Vector3,
	endPoint: Vector3,
	pointCount: number,
): Vector3[] => {
	const stepRatio = 1 / pointCount;
	const points: Vector3[] = [];

	for (let i = 1; i < pointCount; i++) {
		points.push(Vector3.Lerp(startPoint, endPoint, stepRatio * i));
	}

	return points;
};

/**
 * Transforms opening settings into column positions and segments for rail placement.
 * This function calculates the positions of columns along a side, considering openings (gates or doors),
 * and creates segments for rail placement between columns.
 *
 * Steps:
 * 1. Initialize variables: Extract data, set column count, determine left/right points and direction.
 * 2. Handle no opening case: If openingSize is 0, distribute columns evenly between start and end points.
 * 3. Handle opening cases: Adjust column count and positions based on opening position (left, right, middle).
 * 4. Sort positions: Order positions along the direction vector.
 * 5. Create segments: Generate segments between positions, marking 'opening' or 'rail' types.
 *
 * @param {Object} data - Configuration object.
 * @param {number} data.quantity - Total number of columns.
 * @param {Vector3} data.startPoint - Start point of the side.
 * @param {Vector3} data.endPoint - End point of the side.
 * @param {string} data.side - Side ('left', 'right', 'front', 'back').
 * @param {string} data.openingPosition - Opening position ('left', 'right', 'middle').
 * @param {number} data.openingSize - Size of the opening.
 * @param {number} data.offset - Offset for the opening.
 * @returns {Object} Object with positions (Vector3[]) and segments (array of {start, end, type}).
 */
export const transformOpeningSettingToColumnPositions = (
	data,
): { positions: Vector3[]; segments: { start: Vector3; end: Vector3; type: string }[] } => {
	const {
		quantity,
		startPoint,
		endPoint,
		side,
		openingPosition,
		openingSize,
		offset,
		distanceColumn,
		maxDistance,
		originStartPoint,
		originEndPoint,
	} = data;
	if (quantity <= 0) return { positions: [], segments: [] };
	let columnCount = quantity - 2; // minus 2 posts in corner

	const positions: Vector3[] = [];
	const segments: { start: Vector3; end: Vector3; type: string }[] = [];

	const rightPoint = side === 'right' || side === 'back' ? endPoint : startPoint;
	let originRightPoint = side === 'right' || side === 'back' ? originEndPoint : originStartPoint;

	const leftPoint = side === 'right' || side === 'back' ? startPoint : endPoint;
	let originLeftPoint = side === 'right' || side === 'back' ? originStartPoint : originEndPoint;

	if (side === 'left' || side === 'right') {
		originRightPoint = new Vector3(rightPoint.x, rightPoint.y, originRightPoint.z);
		originLeftPoint = new Vector3(leftPoint.x, leftPoint.y, originLeftPoint.z);
	} else {
		originRightPoint = new Vector3(originRightPoint.x, rightPoint.y, rightPoint.z);
		originLeftPoint = new Vector3(originLeftPoint.x, leftPoint.y, rightPoint.z);
	}

	const direction = rightPoint.subtract(leftPoint).normalize();
	const middlePoint = Vector3.Center(leftPoint, rightPoint);

	const halfOpeningSize = openingSize / 2;
	const offsetWithOpening = offset + openingSize;

	let openingStart: Vector3;
	let openingEnd: Vector3;
	if (0 === openingSize) {
		columnCount = columnCount + 2;
		positions.push(...getPointsBetween(leftPoint, rightPoint, columnCount));
	} else {
		if (columnCount <= 0) {
			if (openingPosition === 'middle') {
				columnCount = columnCount + 2;
			} else {
				// a left/right opening needs at least 1 boundary column, even when the side has no middle posts
				columnCount = 1;
			}
		}

		if (columnCount > 0) {
			if ('left' === openingPosition) {
				openingEnd = originLeftPoint.add(direction.scale(offsetWithOpening));

				if (offset !== 0) {
					openingStart = originLeftPoint.add(direction.scale(offset));
					positions.push(openingStart);
					columnCount--;
				} else {
					openingStart = leftPoint;
				}

				const distance = Vector3.Distance(openingEnd, originRightPoint);
				const newQuantity = calculateComponentQuantity(
					distance,
					distanceColumn,
					maxDistance,
					0,
				);
				positions.push(
					openingEnd,
					...getPointsBetween(openingEnd, rightPoint, newQuantity),
				);
			} else if ('right' === openingPosition) {
				openingStart = originRightPoint.add(direction.scale(-offsetWithOpening));

				if (offset !== 0) {
					openingEnd = originRightPoint.add(direction.scale(-offset));
					positions.push(openingEnd);
					columnCount--;
				} else {
					openingEnd = rightPoint;
				}

				const distance = Vector3.Distance(originLeftPoint, openingStart);
				const newQuantity = calculateComponentQuantity(
					distance,
					distanceColumn,
					maxDistance,
					0,
				);

				positions.push(
					openingStart,
					...getPointsBetween(leftPoint, openingStart, newQuantity),
				);
			} else if ('middle' === openingPosition) {
				openingStart = middlePoint.add(direction.scale(-halfOpeningSize));
				openingEnd = middlePoint.add(direction.scale(halfOpeningSize));
				const distance = Vector3.Distance(originLeftPoint, openingStart);
				const newQuantity = calculateComponentQuantity(
					distance,
					distanceColumn,
					maxDistance,
					0,
				);

				positions.push(
					openingStart,
					openingEnd,
					...getPointsBetween(leftPoint, openingStart, newQuantity),
					...getPointsBetween(openingEnd, rightPoint, newQuantity),
				);
			}
		}
	}

	positions.sort(
		(a, b) =>
			Vector3.Dot(a.subtract(leftPoint), direction) -
			Vector3.Dot(b.subtract(leftPoint), direction),
	);

	if (openingStart && openingEnd) {
		const fullPositions = [leftPoint, rightPoint, ...positions];

		fullPositions.sort(
			(a, b) =>
				Vector3.Dot(a.subtract(leftPoint), direction) -
				Vector3.Dot(b.subtract(leftPoint), direction),
		);

		const openingStartDist = Vector3.Dot(openingStart.subtract(leftPoint), direction);
		const openingEndDist = Vector3.Dot(openingEnd.subtract(leftPoint), direction);

		for (let i = 0; i < fullPositions.length - 1; i++) {
			const start = fullPositions[i];
			const end = fullPositions[i + 1];

			const startDist = Vector3.Dot(start.subtract(leftPoint), direction);
			const endDist = Vector3.Dot(end.subtract(leftPoint), direction);

			const type =
				startDist >= openingStartDist && endDist <= openingEndDist ? 'opening' : 'rail';

			segments.push({
				start,
				end,
				type,
			});
		}
	} else {
		const fullPositions = [leftPoint, rightPoint, ...positions];
		fullPositions.sort(
			(a, b) =>
				Vector3.Dot(a.subtract(leftPoint), direction) -
				Vector3.Dot(b.subtract(leftPoint), direction),
		);
		for (let i = 0; i < fullPositions.length - 1; i++) {
			const start = fullPositions[i];
			const end = fullPositions[i + 1];
			segments.push({
				start,
				end,
				type: 'rail',
			});
		}
	}

	return { positions, segments };
};

/**
 * Gets the boundary points for a side, adjusting start and end points based on column width, porch style, and type.
 * This function calculates the effective start and end points for placing components on a side,
 * accounting for column half-width and specific adjustments for porch style and component type.
 *
 * Steps:
 * 1. Extract data parameters.
 * 2. Adjust start and end points based on the side ('front', 'left', 'right', 'back'), applying offsets for column width and porch-specific logic.
 *
 * @param {Object} data - Configuration object.
 * @param {Vector3} data.startPoint - Initial start point.
 * @param {Vector3} data.endPoint - Initial end point.
 * @param {string} data.side - Side ('front', 'left', 'right', 'back').
 * @param {number} data.halfColumnWidth - Half the width of the column.
 * @param {string} data.porchStyle - Porch style ('front' or 'side').
 * @param {string} data.type - Component type (e.g., 'baluster').
 * @returns {Object} Object with adjusted startPoint and endPoint (Vector3).
 */
export const getSideBoundaryPoints = (data) => {
	let { startPoint, endPoint, side, halfColumnWidth, porchStyle, type, porchPosition } = data;
	if (side === 'front') {
		// styles whose front side spans the full model width, so the end column pulls in by a half column
		const fullWidthFrontStyles = [
			// porch options style ids (AddPorchModule.currentPorchStyle)
			'front',
			'l_shape',
			// legacy _shedType styles (SeadevShedBuilder.getPorchStyle())
			'deluxe-corner-left',
			'deluxe-porch-flipped',
			'l-shape-porch',
			'l-shape-porch-right',
		];
		const adjustXEnd = fullWidthFrontStyles.includes(porchStyle)
			? halfColumnWidth
			: type !== 'baluster'
				? 0
				: -halfColumnWidth;
		startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, -halfColumnWidth));
		endPoint = endPoint.add(new Vector3(-adjustXEnd, 0, -halfColumnWidth));
	} else if (side === 'left') {
		const adjustZStart =
			porchStyle === 'side' || porchStyle === 'corner'
				? halfColumnWidth
				: type !== 'baluster'
					? 0
					: -halfColumnWidth;
		if (porchStyle === 'middle') {
			startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, 0));
			endPoint = endPoint.add(new Vector3(halfColumnWidth, 0, 0));
		} else if (porchStyle === 'corner') {
			if (porchPosition === 'left') {
				startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, 0));
				endPoint = endPoint.add(new Vector3(halfColumnWidth, 0, -halfColumnWidth));
			} else {
				startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, halfColumnWidth));
				endPoint = endPoint.add(new Vector3(halfColumnWidth, 0, 0));
			}
		} else if (porchStyle === 'corner-right') {
			startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, halfColumnWidth));
			endPoint = endPoint.add(new Vector3(halfColumnWidth, 0, 0));
		} else {
			startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, adjustZStart));
			endPoint = endPoint.add(new Vector3(halfColumnWidth, 0, -halfColumnWidth));
		}
	} else if (side === 'right') {
		const adjustZStart =
			porchStyle === 'side' ? halfColumnWidth : type !== 'baluster' ? 0 : -halfColumnWidth;
		startPoint = startPoint.add(new Vector3(-halfColumnWidth, 0, adjustZStart));
		endPoint = endPoint.add(new Vector3(-halfColumnWidth, 0, -halfColumnWidth));
	} else if (side === 'back') {
		const adjustXEnd =
			porchStyle === 'front' || porchStyle === 'deluxe'
				? halfColumnWidth
				: type !== 'baluster'
					? 0
					: -halfColumnWidth;
		startPoint = startPoint.add(new Vector3(halfColumnWidth, 0, halfColumnWidth));
		endPoint = endPoint.add(new Vector3(-adjustXEnd, 0, halfColumnWidth));
	}

	return { startPoint, endPoint };
};

/**
 * Clones a mesh template at multiple positions.
 * This function creates instances of a template mesh at specified positions,
 * useful for placing multiple identical objects in a scene.
 *
 * Steps:
 * 1. Enable the template mesh to allow cloning.
 * 2. For each position, create an instance with a unique suffix name and set its position.
 * 3. Disable the template mesh after cloning.
 * 4. Return the array of cloned meshes.
 *
 * @param {Vector3[]} positions - Array of positions to place the clones.
 * @param {Mesh} templateMesh - The mesh to clone.
 * @param {string} suffixName - Suffix for instance names (default '').
 * @returns {Mesh[]} Array of cloned meshes.
 */
export const cloneMeshAtPositions = (positions, templateMesh, suffixName = ''): Mesh[] => {
	let clonedMeshes = [];
	templateMesh.setEnabled(true);
	for (let i = 0; i < positions.length; i++) {
		const clonedMesh = templateMesh.createInstance(`${suffixName}_${i}`);
		clonedMeshes.push(clonedMesh);
		clonedMesh.position = positions[i];
	}
	templateMesh.setEnabled(false);

	return clonedMeshes;
};

/**
 * Sets metadata for column mesh instances.
 * This function assigns side, isOutermost, and braceRotate properties to each mesh instance's metadata.
 *
 * Steps:
 * 1. Iterate through each mesh instance.
 * 2. Assign metadata object with side, isOutermost (false), and braceRotate ('left').
 *
 * @param {Mesh[]} instances - Array of mesh instances.
 * @param {string} side - The side of the columns.
 */
export const setColumnInstancesMetadata = (instances, side) => {
	for (let i = 0; i < instances.length; i++) {
		instances[i].metadata = {
			side,
			isOutermost: false,
			braceRotate: 'left',
		};
	}
};

/**
 * Checks if a side has a fixed position based on porch style.
 * Returns true if the side is fixed for the given porch style.
 *
 * Steps:
 * 1. Check if porchStyle is 'front' and side is 'front'.
 * 2. Check if porchStyle is 'side' and side is 'left'.
 * 3. Return false otherwise.
 *
 * @param {string} porchStyle - The porch style ('front' or 'side').
 * @param {string} side - The side ('front', 'left', 'right', 'back').
 * @returns {boolean} True if the side is fixed, false otherwise.
 */
export const isSideFixedPosition = (porchStyle, side) => {
	if (porchStyle === 'front' && side === 'front') return true;
	if (porchStyle === 'side' && side === 'left') return true;
	if (porchStyle === 'corner' && side === 'left') return true;
	if (porchStyle === 'l_shape' && side === 'front') return true;
	if (porchStyle === 'corner-left' && side === 'left') return true;
	if (porchStyle === 'corner-right' && side === 'left') return true; // side here is main side of model
	if (porchStyle === 'deluxe-corner-left' && side === 'front') return true;
	if (porchStyle === 'deluxe-porch-flipped' && side === 'front') return true;
	if (porchStyle === 'l-shape-porch' && side === 'front') return true;
	if (porchStyle === 'l-shape-porch-right' && side === 'front') return true;

	return false;
};

/**
 * Extracts distance and maxDistance settings from available columns for a specific side.
 * @param {string} side - The side ('front', 'left', 'right', 'back')
 * @param {Array} availableColumn - Array of column configurations
 * @param {string} porchStyle - The porch style for side mapping
 * @returns {Object} { distanceColumn, maxDistanceColumn }
 */
export const getDistanceSettingsForSide = (side: string, availableColumn: Array<any>) => {
	let distanceColumn = 0;
	let maxDistanceColumn = 0;
	// const sideConvert = mappingSideRevert[porchStyle]?.[side];
	const findColumnIndex = availableColumn.findIndex((item) => item.sides.includes(side));
	if (findColumnIndex !== -1) {
		distanceColumn = availableColumn[findColumnIndex].post_distance;
		maxDistanceColumn = availableColumn[findColumnIndex].max_distance;
	}
	return { distanceColumn, maxDistanceColumn };
};

/**
 * Determines opening settings (size, position, offset) for a specific side.
 * If the opening doesn't affect this side, returns openingSize as 0.
 * @param {Object} opening - Opening configuration
 * @param {string} side - The side to check
 * @returns {Object} { openingSize, openingPosition, openingOffset }
 */
export const getOpeningConfigForSide = (opening: any, side: string) => {
	let openingSize = 0;
	let openingPosition = '';
	let openingOffset = 0;

	if (opening) {
		const found = opening.find((item) => {
			if (Array.isArray(item.side)) {
				return item.side.includes(side);
			}
			return item.side === side;
		});
		if (found) {
			openingSize = Number(found.size) || 0;
			openingPosition = found.position || '';
			openingOffset = Number(found.offset) || 0;
		}
	}

	return { openingSize, openingPosition, openingOffset };
};

/**
 * Calculates the quantity of components needed for a segment based on distance rules.
 * @param {number} distance - Total distance of the segment
 * @param {number} distanceColumn - Target distance per component
 * @param {number} maxDistanceColumn - Maximum allowed distance
 * @returns {number} Calculated quantity
 */
export const calculateComponentQuantity = (
	distance: number,
	distanceColumn: number,
	maxDistanceColumn: number,
	openingSize: number,
): number => {
	if (distanceColumn <= 0 || openingSize >= distance) return 0;

	const remainder = distance % distanceColumn;
	let quantity =
		openingSize > 0
			? Math.ceil(distance / distanceColumn)
			: Math.floor(distance / distanceColumn);
	const errNum = remainder / quantity;

	return errNum + distanceColumn > maxDistanceColumn ? quantity + 1 : quantity;
};

/**
 * Gets the wall drawing data for the porch based on the porch style.
 * Returns wall configurations for front or side porches.
 * @param {string} porchStyle - The style of the porch ('front' or 'side').
 * @returns {Array} Array of wall data for porch.
 */
export const getDataDrawWallPorch = (porchStyle, roofShape, position) => {
	const typeRoof = roofShape === 'gambrel' ? 'gambrel' : 'aframe';
	switch (porchStyle) {
		case 'front':
			return dataDrawWallFrontPorch[typeRoof];
		case 'side':
			return dataDrawWallSidePorch[typeRoof];
		case 'middle':
			return dataDrawWallMiddlePorch[typeRoof];
		case 'corner':
			return dataDrawWallCornerPorch[position][typeRoof];
		case 'l_shape':
			return dataDrawWallLShapePorch[position][typeRoof];
	}
};

/**
 * Gets the corner trim data specific to the porch style.
 * Returns bone start and end points for corner trim placement.
 * @returns {Array} Array of corner trim configurations.
 */
export const getDataCornerTrimPorch = (porchStyle, position) => {
	switch (porchStyle) {
		case 'front':
			return dataCornerTrimFrontPorch;
		case 'side':
			return dataCornerTrimSidePorch;
		case 'middle':
			return dataCornerTrimMiddlePorch;
		case 'corner':
			return dataCornerTrimCornerPorch[position];
		case 'l_shape':
			return dataCornerTrimLShapePorch[position];
	}
};

/**
 * Applies material to a mesh, creating it if it doesn't exist, and adjusts UV scaling.
 * @param {object} data - The data including mesh, materialName, materialInfo, and optional color.
 */
export const applyMaterial = (data) => {
	const { mesh, materialName, materialInfo, color, scene } = data;
	let material = scene.getMaterialByName(materialName);
	if (!material) {
		const colorData = { type: 'color', code: color ? color : '', image: '', texture: '' };
		material = helpers.createMaterial(materialName, colorData, materialInfo, scene);
	}
	mesh.material = material;
	const meshWidth = helpers.getMeshWidth(mesh);
	const meshHeight = helpers.getMeshLength(mesh);
	adjustUVMaterial({
		meshWidth,
		meshHeight,
		material,
		initVScale: materialInfo.vScale,
		initUScale: materialInfo.uScale,
	});
};

/**
 * Adjusts the UV scaling for the material based on mesh dimensions.
 * @param {object} data - The data including meshHeight, meshWidth, material, initVScale, and initUScale.
 */
export const adjustUVMaterial = (data) => {
	const { meshHeight, meshWidth, material, initVScale, initUScale } = data;
	const vScale = +(meshHeight / (8 / initVScale)).toFixed(3); // 8 is default width of material when design create. this._sidingVscale is init V scale from network
	const uScale = +(meshWidth / (8 / initUScale)).toFixed(3);

	material.bumpTexture['vScale'] = vScale;
	material.diffuseTexture['vScale'] = vScale;
	material.specularTexture['vScale'] = vScale;

	material.bumpTexture['uScale'] = uScale;
	material.diffuseTexture['uScale'] = uScale;
	material.specularTexture['uScale'] = uScale;

	if (material.opacityTexture) {
		material.opacityTexture['vScale'] = vScale;
		material.opacityTexture['uScale'] = uScale;
	}
};

/**
 * Moves bones for front porch style.
 * @param {object} data - The data including porchConfig, baseDepth, baseHeight, baseWidth, and bones.
 */
export const moveBoneFrontPorch = (data) => {
	const { porchConfig, baseDepth, baseHeight, baseWidth, bones } = data;
	const porchDepth = Number(porchConfig?.depth);
	const haftDepth = baseDepth / 2;
	if (bones['b_fp_l1']) {
		bones['b_fp_l1'].position = new Vector3(
			bones['b_f_l1'].position.x,
			bones['b_f_l1'].position.y,
			haftDepth - porchDepth,
		);
	}
	if (bones['b_fp_l2']) {
		bones['b_fp_l2'].position = new Vector3(
			bones['b_f_l2'].position.x,
			bones['b_f_l2'].position.y,
			haftDepth - porchDepth,
		);
	}
	if (bones['b_fp_r1']) {
		bones['b_fp_r1'].position = new Vector3(
			bones['b_f_r1'].position.x,
			bones['b_f_r1'].position.y,
			haftDepth - porchDepth,
		);
	}
	if (bones['b_fp_r2']) {
		bones['b_fp_r2'].position = new Vector3(
			bones['b_f_r2'].position.x,
			bones['b_f_r2'].position.y,
			haftDepth - porchDepth,
		);
	}
};

/**
 * Moves bones for side porch style.
 * @param {object} data - The data including porchConfig, baseDepth, baseHeight, baseWidth, and bones.
 */
export const moveBoneSidePorch = (data) => {
	const { porchConfig, baseDepth, baseHeight, baseWidth, bones } = data;
	const porchDepth = Number(porchConfig?.depth);
	const haftWidth = baseWidth / 2;
	if (bones['b_sp_r1']) {
		bones['b_sp_r1'].position = new Vector3(
			-haftWidth + porchDepth,
			bones['b_f_l1'].position.y,
			bones['b_f_l1'].position.z,
		);
	}
	if (bones['b_sp_r2']) {
		bones['b_sp_r2'].position = new Vector3(
			-haftWidth + porchDepth,
			bones['b_f_l2'].position.y,
			bones['b_f_l2'].position.z,
		);
	}
	if (bones['b_sp_l1']) {
		bones['b_sp_l1'].position = new Vector3(
			-haftWidth + porchDepth,
			bones['b_b_l1'].position.y,
			bones['b_b_l1'].position.z,
		);
	}
	if (bones['b_sp_l2']) {
		bones['b_sp_l2'].position = new Vector3(
			-haftWidth + porchDepth,
			bones['b_b_l2'].position.y,
			bones['b_b_l2'].position.z,
		);
	}
};

/**
 * Moves bones for middle porch style.
 * @param {object} data - The data including porchConfig, baseDepth, baseHeight, baseWidth, and bones.
 */
export const moveBoneMiddlePorch = (data) => {
	const { porchConfig, baseDepth, baseHeight, baseWidth, bones } = data;
	const porchDepth = Number(porchConfig?.depth);
	const porchWidth = Number(porchConfig?.width);
	const haftWidth = Number(baseWidth) / 2;
	const positionX = -haftWidth + porchDepth;

	if (bones['b_p_b_l1']) {
		bones['b_p_b_l1'].position = new Vector3(
			bones['b_b_l1'].position.x,
			bones['b_b_l1'].position.y,
			-porchWidth / 2,
		);
	}

	if (bones['b_p_b_l2']) {
		bones['b_p_b_l2'].position = new Vector3(
			bones['b_b_l2'].position.x,
			bones['b_b_l2'].position.y,
			-porchWidth / 2,
		);
	}

	if (bones['b_p_b_r1']) {
		bones['b_p_b_r1'].position = new Vector3(
			positionX,
			bones['b_b_l1'].position.y,
			-porchWidth / 2,
		);
	}

	if (bones['b_p_b_r2']) {
		bones['b_p_b_r2'].position = new Vector3(
			positionX,
			bones['b_b_l2'].position.y,
			-porchWidth / 2,
		);
	}

	if (bones['b_p_f_l1']) {
		bones['b_p_f_l1'].position = new Vector3(
			bones['b_f_l1'].position.x,
			bones['b_f_l1'].position.y,
			porchWidth / 2,
		);
	}

	if (bones['b_p_f_l2']) {
		bones['b_p_f_l2'].position = new Vector3(
			bones['b_f_l2'].position.x,
			bones['b_f_l2'].position.y,
			porchWidth / 2,
		);
	}

	if (bones['b_p_f_r1']) {
		bones['b_p_f_r1'].position = new Vector3(
			positionX,
			bones['b_f_l1'].position.y,
			porchWidth / 2,
		);
	}

	if (bones['b_p_f_r2']) {
		bones['b_p_f_r2'].position = new Vector3(
			positionX,
			bones['b_f_l2'].position.y,
			porchWidth / 2,
		);
	}
};

/**
 * Moves bones for middle porch style.
 * @param {object} data - The data including porchConfig, baseDepth, baseHeight, baseWidth, and bones.
 */
export const moveBoneLShapePorch = (data) => {
	const { porchConfig, baseDepth, baseHeight, baseWidth, bones } = data;
	const porchDepth = Number(porchConfig?.depth);
	const alleyWidth = Number(porchConfig?.alley_width);
	const sideWallWidth = Number(porchConfig?.side_wall_width);
	const position = porchConfig?.position || 'left';
	const haftWidth = Number(baseWidth) / 2;
	const haftDepth = Number(baseDepth) / 2;
	if (position === 'left') {
		if (bones['b_p_f_l1']) {
			bones['b_p_f_l1'].position = new Vector3(
				bones['b_f_l1'].position.x,
				bones['b_f_l1'].position.y,
				haftDepth - porchDepth,
			);
		}
		if (bones['b_p_f_l2']) {
			bones['b_p_f_l2'].position = new Vector3(
				bones['b_f_l2'].position.x,
				bones['b_f_l2'].position.y,
				haftDepth - porchDepth,
			);
		}
		if (bones['b_p_f_r1']) {
			bones['b_p_f_r1'].position = new Vector3(
				haftWidth - alleyWidth,
				bones['b_f_l1'].position.y,
				haftDepth - porchDepth,
			);
		}
		if (bones['b_p_f_r2']) {
			bones['b_p_f_r2'].position = new Vector3(
				haftWidth - alleyWidth,
				bones['b_f_l2'].position.y,
				haftDepth - porchDepth,
			);
		}

		if (bones['b_p_b_r1']) {
			bones['b_p_b_r1'].position = new Vector3(
				bones['b_f_r1'].position.x,
				bones['b_f_r1'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}
		if (bones['b_p_b_r2']) {
			bones['b_p_b_r2'].position = new Vector3(
				bones['b_f_r2'].position.x,
				bones['b_f_r2'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}

		if (bones['b_p_b_l1']) {
			bones['b_p_b_l1'].position = new Vector3(
				haftWidth - alleyWidth,
				bones['b_f_r1'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}
		if (bones['b_p_b_l2']) {
			bones['b_p_b_l2'].position = new Vector3(
				haftWidth - alleyWidth,
				bones['b_f_r2'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}
	} else {
		if (bones['b_p_f_r1']) {
			bones['b_p_f_r1'].position = new Vector3(
				bones['b_f_r1'].position.x,
				bones['b_f_r1'].position.y,
				haftDepth - porchDepth,
			);
		}
		if (bones['b_p_f_r2']) {
			bones['b_p_f_r2'].position = new Vector3(
				bones['b_f_r2'].position.x,
				bones['b_f_r2'].position.y,
				haftDepth - porchDepth,
			);
		}

		if (bones['b_p_f_l1']) {
			bones['b_p_f_l1'].position = new Vector3(
				-haftWidth + alleyWidth,
				bones['b_f_r1'].position.y,
				haftDepth - porchDepth,
			);
		}
		if (bones['b_p_f_l2']) {
			bones['b_p_f_l2'].position = new Vector3(
				-haftWidth + alleyWidth,
				bones['b_f_r2'].position.y,
				haftDepth - porchDepth,
			);
		}

		if (bones['b_p_b_l1']) {
			bones['b_p_b_l1'].position = new Vector3(
				bones['b_f_l1'].position.x,
				bones['b_f_l1'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}
		if (bones['b_p_b_l2']) {
			bones['b_p_b_l2'].position = new Vector3(
				bones['b_f_l2'].position.x,
				bones['b_f_l2'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}

		if (bones['b_p_b_r1']) {
			bones['b_p_b_r1'].position = new Vector3(
				-haftWidth + alleyWidth,
				bones['b_f_r1'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}
		if (bones['b_p_b_r2']) {
			bones['b_p_b_r2'].position = new Vector3(
				-haftWidth + alleyWidth,
				bones['b_f_r2'].position.y,
				haftDepth - porchDepth - sideWallWidth,
			);
		}
	}
};

/**
 * Moves bones for middle porch style.
 * @param {object} data - The data including porchConfig, baseDepth, baseHeight, baseWidth, and bones.
 */
export const moveBoneCornerPorch = (data) => {
	const { porchConfig, baseDepth, baseHeight, baseWidth, bones } = data;
	const porchDepth = Number(porchConfig?.depth);
	const porchWidth = Number(porchConfig?.width);
	const position = porchConfig?.position || 'left';
	const haftWidth = Number(baseWidth) / 2;
	const haftDepth = Number(baseDepth) / 2;
	const positionX = -haftWidth + porchDepth;
	const positionZ = haftDepth - porchWidth;

	if (position === 'left') {
		if (bones['b_p_b_l1']) {
			bones['b_p_b_l1'].position = new Vector3(
				bones['b_b_l1'].position.x,
				bones['b_b_l1'].position.y,
				positionZ,
			);
		}

		if (bones['b_p_b_l2']) {
			bones['b_p_b_l2'].position = new Vector3(
				bones['b_b_l2'].position.x,
				bones['b_b_l2'].position.y,
				positionZ,
			);
		}

		if (bones['b_p_b_r1']) {
			bones['b_p_b_r1'].position = new Vector3(
				positionX,
				bones['b_b_l1'].position.y,
				positionZ,
			);
		}

		if (bones['b_p_b_r2']) {
			bones['b_p_b_r2'].position = new Vector3(
				positionX,
				bones['b_b_l2'].position.y,
				positionZ,
			);
		}

		if (bones['b_p_f_r1']) {
			bones['b_p_f_r1'].position = new Vector3(
				positionX,
				bones['b_f_l1'].position.y,
				bones['b_f_l1'].position.z,
			);
		}

		if (bones['b_p_f_r2']) {
			bones['b_p_f_r2'].position = new Vector3(
				positionX,
				bones['b_f_l2'].position.y,
				bones['b_f_l1'].position.z,
			);
		}
	} else {
		if (bones['b_p_f_l1']) {
			bones['b_p_f_l1'].position = new Vector3(
				bones['b_b_l1'].position.x,
				bones['b_b_l1'].position.y,
				-positionZ,
			);
		}

		if (bones['b_p_f_l2']) {
			bones['b_p_f_l2'].position = new Vector3(
				bones['b_b_l2'].position.x,
				bones['b_b_l2'].position.y,
				-positionZ,
			);
		}

		if (bones['b_p_f_r1']) {
			bones['b_p_f_r1'].position = new Vector3(
				positionX,
				bones['b_b_l1'].position.y,
				-positionZ,
			);
		}

		if (bones['b_p_f_r2']) {
			bones['b_p_f_r2'].position = new Vector3(
				positionX,
				bones['b_b_l2'].position.y,
				-positionZ,
			);
		}

		if (bones['b_p_b_r1']) {
			bones['b_p_b_r1'].position = new Vector3(
				positionX,
				bones['b_b_l1'].position.y,
				bones['b_b_l1'].position.z,
			);
		}

		if (bones['b_p_b_r2']) {
			bones['b_p_b_r2'].position = new Vector3(
				positionX,
				bones['b_b_l2'].position.y,
				bones['b_b_l1'].position.z,
			);
		}
	}
};

/**
 * Prepares the wall drawing data by merging porch walls with origin data.
 * Updates or adds porch walls to the existing wall data.
 * @param {Array} wallPorch - The porch wall data.
 * @param {Array} originDataDrawWall - The original wall data.
 * @returns {Array} The merged wall data.
 */
export const prepareDataDrawWall = (wallPorch, originDataDrawWall) => {
	let result = [...wallPorch];
	let other = [];
	originDataDrawWall.forEach((newWall) => {
		const index = result.findIndex((item) => item.side === newWall.side);
		if (index === -1) {
			other.push(newWall);
		}
	});

	return [...result, ...other];
};

/**
 * Disposes all meshes in the provided array.
 * @param {Array} meshes - Array of meshes to dispose.
 */
export const disposeMeshes = (meshes) => {
	meshes.forEach((mesh) => {
		mesh.dispose();
	});
};

/**
 * Creates a standard faceUV array for wall drawing.
 * @returns {Array} Array of Vector4 for face UVs.
 */
export const createFaceUV = () => {
	return [
		new Vector4(0, 0, 1, 1),
		new Vector4(0, 0, 1, 1),
		new Vector4(0, 0, 1, 1),
		new Vector4(0, 0, 1, 1),
		new Vector4(0, 0, 1, 1),
		new Vector4(0, 0, 1, 1),
	];
};

/**
 * Gets the data for drawing walls based on porch style.
 * @param {string} porchStyle - The porch style ('front' or 'side').
 * @param {string} wallType - The type of wall ('ceiling' or 'deck').
 * @param {string} position.
 * @returns {Array} Array of wall data.
 */
export const getDataDrawWallByType = (porchStyle, wallType, position) => {
	if (wallType === 'ceiling') {
		switch (porchStyle) {
			case 'front':
			case 'side':
			case 'middle':
				return getDataDrawCeiling(porchStyle, position);
			case 'corner':
			case 'l_shape':
				return getDataDrawCeiling(porchStyle, position);
		}
	} else if (wallType === 'deck') {
		switch (porchStyle) {
			case 'front':
			case 'side':
			case 'middle':
				return getDataDrawDeck(porchStyle, position);
			case 'corner':
			case 'l_shape':
				return getDataDrawDeck(porchStyle, position);
		}
	}
	return [];
};

/**
 * Gets the data for drawing the ceiling wall based on the current porch style.
 * @param {string} porchStyle - The porch style.
 * @param {string} position.
 * @returns {Array} Array of wall data for ceiling.
 */
const getDataDrawCeiling = (porchStyle, position) => {
	if (porchStyle === 'front') {
		const data = [
			{
				side: 'ceiling',
				name: 'ceiling_wall',
				oldName: '',
				position: 'b_fp_r2',
				bones: [
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_FP_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_FP_L2',
						x: 0,
						y: 0,
					},
				],
			},
		];

		return data; // Note: would need shedBuilder.formatWallInfoFromAPI(data) in main
	} else if (porchStyle === 'side') {
		const data = [
			{
				side: 'ceiling',
				name: 'ceiling_wall',
				oldName: '',
				position: 'b_sp_r2',
				bones: [
					{
						name: 'b_sp_r2',
						x: 0,
						y: 0,
					},
					{
						name: 'b_f_l2',
						x: 0,
						y: 0,
					},
					{
						name: 'b_b_l2',
						x: 0,
						y: 0,
					},
					{
						name: 'b_sp_l2',
						x: 0,
						y: 0,
					},
				],
			},
		];

		return data;
	} else if (porchStyle === 'middle') {
		const data = [
			{
				side: 'ceiling',
				name: 'ceiling_wall',
				oldName: '',
				position: 'b_p_b_l2',
				offset: 0,
				bones: [
					{
						name: 'b_p_b_l2',
						x: 0,
						y: 0,
					},
					{
						name: 'b_p_b_r2',
						x: 0,
						y: 0,
					},
					{
						name: 'b_p_f_r2',
						x: 0,
						y: 0,
					},
					{
						name: 'b_p_f_l2',
						x: 0,
						y: 0,
					},
				],
			},
		];

		return data;
	} else if (porchStyle === 'corner') {
		if (position === 'left') {
			const data = [
				{
					side: 'ceiling',
					name: 'ceiling_wall',
					oldName: '',
					position: 'b_f_l2',
					offset: 0,
					bones: [
						{
							name: 'B_F_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_L2',
							x: 0,
							y: 0,
						},
					],
				},
			];

			return data;
		} else {
			const data = [
				{
					side: 'ceiling',
					name: 'ceiling_wall',
					oldName: '',
					position: 'b_b_l2',
					offset: 0,
					bones: [
						{
							name: 'B_B_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_L2',
							x: 0,
							y: 0,
						},
					],
				},
			];

			return data;
		}
	} else if (porchStyle === 'l_shape') {
		if (position === 'left') {
			const data = [
				{
					side: 'ceiling',
					name: 'ceiling_wall',
					oldName: '',
					position: 'b_f_l2',
					offset: 0,
					bones: [
						{
							name: 'B_P_F_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_L2',
							x: 0,
							y: 0,
						},
					],
				},
			];
			return data;
		} else {
			const data = [
				{
					side: 'ceiling',
					name: 'ceiling_wall',
					oldName: '',
					position: 'b_f_l2',
					offset: 0,
					bones: [
						{
							name: 'B_P_F_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_L2',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_R2',
							x: 0,
							y: 0,
						},
					],
				},
			];
			return data;
		}
	}

	return [];
};

/**
 * Gets the data for drawing the deck wall based on the current porch style.
 * @param {string} porchStyle - The porch style.
 * @returns {Array} Array of wall data for deck.
 */
const getDataDrawDeck = (porchStyle, position) => {
	if (porchStyle === 'front') {
		const data = [
			{
				side: 'deck',
				name: 'deck_wall',
				oldName: '',
				position: 'b_fp_r1',
				offset: 0.11,
				bones: [
					{
						name: 'B_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_FP_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_FP_L1',
						x: 0,
						y: 0,
					},
				],
			},
		];
		return data;
	} else if (porchStyle === 'side') {
		const data = [
			{
				side: 'deck',
				name: 'deck_wall',
				oldName: '',
				position: 'b_sp_r1',
				offset: 0.11,
				bones: [
					{
						name: 'b_sp_r1',
						x: 0,
						y: 0,
					},
					{
						name: 'b_f_l1',
						x: 0,
						y: 0,
					},
					{
						name: 'b_b_l1',
						x: 0,
						y: 0,
					},
					{
						name: 'b_sp_l1',
						x: 0,
						y: 0,
					},
				],
			},
		];

		return data;
	} else if (porchStyle === 'middle') {
		const data = [
			{
				side: 'deck',
				name: 'deck_wall',
				oldName: '',
				position: 'b_p_b_l1',
				offset: 0.11,
				bones: [
					{
						name: 'b_p_b_l1',
						x: 0,
						y: 0,
					},
					{
						name: 'b_p_b_r1',
						x: 0,
						y: 0,
					},
					{
						name: 'b_p_f_r1',
						x: 0,
						y: 0,
					},
					{
						name: 'b_p_f_l1',
						x: 0,
						y: 0,
					},
				],
			},
		];

		return data;
	} else if (porchStyle === 'corner') {
		if (position === 'left') {
			const data = [
				{
					side: 'deck',
					name: 'deck_wall',
					oldName: '',
					position: 'b_f_l1',
					offset: 0.11,
					bones: [
						{
							name: 'B_F_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_L1',
							x: 0,
							y: 0,
						},
					],
				},
			];

			return data;
		} else {
			const data = [
				{
					side: 'deck',
					name: 'deck_wall',
					oldName: '',
					position: 'b_b_l1',
					offset: 0.11,
					bones: [
						{
							name: 'B_B_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_L1',
							x: 0,
							y: 0,
						},
					],
				},
			];

			return data;
		}
	} else if (porchStyle === 'l_shape') {
		if (position === 'left') {
			const data = [
				{
					side: 'deck',
					name: 'deck_wall',
					oldName: '',
					position: 'b_b_l1',
					offset: 0.11,
					bones: [
						{
							name: 'B_P_F_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_L1',
							x: 0,
							y: 0,
						},
					],
				},
			];
			return data;
		} else {
			const data = [
				{
					side: 'deck',
					name: 'deck_wall',
					oldName: '',
					position: 'b_b_l1',
					offset: 0.11,
					bones: [
						{
							name: 'B_P_F_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_F_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_R1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_P_B_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_L1',
							x: 0,
							y: 0,
						},
						{
							name: 'B_F_R1',
							x: 0,
							y: 0,
						},
					],
				},
			];
			return data;
		}
	}

	return [];
};

/**
 * Moves bones based on porch style and dimensions to position porch elements correctly.
 * @param {object} data - The data including porchConfig, baseDepth, baseHeight, baseWidth, and bones.
 */
export const handleMoveBoneAddPorch = (data) => {
	const { porchConfig } = data;
	const porchStyle = porchConfig?.style;
	switch (porchStyle) {
		case 'front':
			moveBoneFrontPorch(data);
			break;
		case 'side':
			moveBoneSidePorch(data);
			break;
		case 'middle':
			moveBoneMiddlePorch(data);
			break;
		case 'corner':
			moveBoneCornerPorch(data);
			break;
		case 'l_shape':
			moveBoneLShapePorch(data);
			break;
		default:
			break;
	}
};

export const getDoorInMainSideOldPorch = (allComponent, oldPorchStyle) => {
	const mainSide = mainSidePlaceDoor[oldPorchStyle];
	if (!mainSide) return {};
	const doorInMain = Object.values(allComponent).filter(
		(item: any) => item.side === mainSide && item.type === 'door',
	);
	if (doorInMain.length > 0) {
		return doorInMain[0];
	}

	return {};
};

export const isHasDoorInMain = (allComponent, porchStyle) => {
	const mainSide = mainSidePlaceDoor[porchStyle];

	if (!mainSide) return false;

	const doorInMain = Object.values(allComponent).filter(
		(item: any) => item.side === mainSide && item.type === 'door',
	);

	return doorInMain.length > 0;
};

/**
 * Finds the first valid new side for a component based on position and dimensions.
 * Steps:
 * 1. Iterate through possible new sides from mapping.
 * 2. Build plane context for each side.
 * 3. Check if component fits inside the plane.
 * 4. Return the valid side or null if none found.
 */
export const findValidNewSideForComponent = (
	side: string,
	anchor: string,
	componentInfo: any,
	shedBuilder: any,
): string | null => {
	const newSides = swapComponentSideMapping?.[side];
	if (!newSides) return null;
	const { position, componentWidth, componentHeight } = componentInfo;
	for (let i = 0; i < newSides.length; i++) {
		const newSide = newSides[i];
		const args = {
			side: newSide,
			targetPosition: position,
			component: componentInfo,
			componentWidth: componentWidth,
			componentHeight: componentHeight,
			anchor,
		};
		const isInside = shedBuilder.isComponentInSideMeshes(args);
		if (isInside) {
			return newSide;
		}
	}

	return null;
};

export const swapMaterials = (material: StandardMaterial) => {
	if (material.emissiveTexture != null && material.emissiveTexture != undefined) {
		material.ambientTexture = material.emissiveTexture;
		material.emissiveTexture = null;
	}
};

export const transformStartPointCloneBaluster = (data) => {
	const { point, porchStyle, side, halfColumnWidth, isAdjacentWall } = data;
	if (!isAdjacentWall) return point;

	if (porchStyle === 'front' && (side === 'left' || side === 'right')) {
		return point.add(new Vector3(0, 0, -halfColumnWidth));
	}
	if (porchStyle === 'side' && (side === 'front' || side === 'back')) {
		return point.add(new Vector3(halfColumnWidth, 0, 0));
	}

	return point;
};

export const isEmptyComponentFullWallOnSide = (newSide, componentKey, allComponents) => {
	for (const k in allComponents) {
		const { key, side, isFullWall, type } = allComponents?.[k];
		if (
			key !== componentKey &&
			side === newSide &&
			isFullWall &&
			(type === 'shelf' || type === 'workbench')
		) {
			return false;
		}
	}

	return true;
};

// export const findValidNewSideForComponentOLD = (
// 	side: string,
// 	position: any,
// 	anchor: string,
// 	dimension: TDimension2D,
// 	shedBuilder: any,
// ): string | null => {
// 	const newSides = swapComponentSideMapping?.[side];
// 	if (!newSides) return null;

// 	for (let i = 0; i < newSides.length; i++) {
// 		const newSide = newSides[i];
// 		const ctx: TPlaneContext = shedBuilder.buildPlaneContext(newSide);
// 		if (!ctx.origin) continue;

// 		const isInside = helpers.isInsidePlane({
// 			position3D: new Vector3(position.x, position.y, position.z),
// 			ctx,
// 			dimension,
// 			anchor,
// 		});

// 		if (isInside) {
// 			return newSide;
// 		}
// 	}

// 	return null;
// };

// Returns the appropriate side-flip configuration for the model
export const getSidesFlipModelOption = (porchStyle) => {
	if (porchStyle === 'deluxe') {
		return SWAP_SIDE_DELUXE_PORCH;
	}

	if (porchStyle === 'l_shape') {
		return SWAP_SIDE_L_SHAPE_PORCH;
	}

	if (porchStyle === 'corner') {
		return SWAP_SIDE_CORNER_PORCH;
	}

	return [];
};

/**
 * Convert a side to its corresponding side in the given sides array.
 *
 * @param {object} sides - An object with side names as keys and their corresponding side names as values.
 * @param {string} side - The side to convert.
 *
 * @returns {string} The converted side.
 */
export const convertSides = (sides, side) => {
	return undefined !== sides[side] ? sides[side] : side;
};

/**
 * Returns the list of sides that should be flipped based on the current porch type.
 *
 * @returns {string[]} An array of side identifiers that should be flipped.
 */
const getFlipSides = (porchStyle) => {
	if (porchStyle === 'deluxe') {
		return [
			'left',
			'right',
			'diagonal_right',
			'diagonal_left',
			'inner_left',
			'inner_right',
			'inner_front',
			'front',
		];
	}

	if (porchStyle === 'l_shape') {
		return ['left', 'right', 'inner_left', 'inner_right', 'inner_front', 'front'];
	}

	if (porchStyle === 'corner') {
		return ['left', 'inner_left', 'front', 'inner_front', 'back', 'inner_back'];
	}

	return [];
};
/**
 * Calculates the flipped position for a given component based on porch type and side.
 *
 * @param {string} porchStyle - The porchStyle .
 * @param {string} side - The current side of the component (e.g. 'left', 'front', etc.).
 * @param {Vector3} position - The original position of the component.
 * @returns {Vector3} A new Vector3 representing the flipped position, or the original if no flip is required.
 */
export const getFlippedPositionForComponent = (
	porchStyle: string,
	side: string,
	position: Vector3,
	anchorPosition: Vector3,
) => {
	const flipSides = getFlipSides(porchStyle);

	// If this side doesn't need flipping, return the original position
	if (!flipSides.includes(side)) {
		return position;
	}

	// Models flip along the X axis
	const shouldFlipX = porchStyle === 'deluxe' || porchStyle === 'l_shape';
	if (shouldFlipX) {
		if (porchStyle === 'l_shape') {
			if (side === 'inner_left' || side === 'inner_right') {
				return new Vector3(anchorPosition.x, position.y, position.z);
			} else if (side === 'inner_front' || side === 'front') {
				return new Vector3(-position.x, position.y, anchorPosition.z);
			} else {
				return new Vector3(-position.x, position.y, position.z);
			}
		}
		return new Vector3(-position.x, position.y, position.z);
	}

	if (porchStyle === 'corner') {
		if (side === 'inner_left') {
			return new Vector3(anchorPosition.x, position.y, -position.z);
		} else if (side === 'inner_back' || side === 'inner_front') {
			return new Vector3(anchorPosition.x, position.y, anchorPosition.z);
		} else {
			return new Vector3(position.x, position.y, -position.z);
		}
	}

	return position;
};

export const getAnchorPosition = (newSide, componentInfo, shedBuilder, isMoveAlongFloor) => {
	const { position, meshes, name, type, parent, componentWidth, componentHeight } = componentInfo;
	const wallPosition = shedBuilder.getWallBones(newSide);
	let boundingBox = helpers.filterByValue(meshes, 'name', '_bb_obj') as Mesh;
	const data = {
		side: newSide,
		type,
		meshesInfo: wallPosition,
		componentWidth,
		componentHeight,
		componentName: name,
		boundingBox: boundingBox,
		firstMesh: meshes[0],
		isValidTackRoom: false,
		isTackDoor: true,
		isMoveAlongFloor,
	};

	const tempPosition = shedBuilder.getNewPosition(data);
	if (tempPosition) return tempPosition;

	// getNewPosition can return false (e.g. fixed-height windows with no door left to follow).
	// Fall back to the wall center: both wall bones share the perpendicular coordinate
	// (what getNewPosition uses as fixedPosition), and the midpoint matches its `middle`,
	// which callers use for full-wall components.
	const left = shedBuilder._bones[wallPosition?.left];
	const right = shedBuilder._bones[wallPosition?.right];
	if (left && right) {
		const center = Vector3.Center(left.position, right.position);
		return new Vector3(center.x, position.y, center.z);
	}

	return new Vector3(position.x, position.y, position.z);
};

export const hasPresetPorch = (): boolean => {
	return jQuery('.group-upgrade-porch-preset').length ? true : false;
};

const IN_MODEL_PORCH_MESH_NAMES = [
	'rail_h_r_obj',
	'rail_v_r_obj',
	'rail_h_l_obj',
	'rail_v_l_obj',
	'posts_braces_obj',
	'column_braces_obj',
	'column_obj',
	'baluster_obj',
	'posts_obj',
	'rail_obj',
	'column_gable_end_obj',
	'column_braces_gable_end_obj',
	'column_side_end_obj',
	'column_braces_side_end_obj',
];

export const removePorchObject = (meshes: AbstractMesh[]) => {
	for (let i = 0; i < meshes.length; i++) {
		if (IN_MODEL_PORCH_MESH_NAMES.includes(meshes[i].name)) {
			meshes[i].dispose(true, false);
		}
	}
};

export const isNoRailing = () => {
	return jQuery('[name="ssb-group-porch-preset-railing"]:checked').val() === 'no-railing';
};

/**
 * Railing off for the dynamic porch: the preset railing radio when the model has presets,
 * otherwise the With/No Railing toggle when rendered.
 */
export const isPorchRailingOff = () => {
	if (hasPresetPorch()) return isNoRailing();

	const toggle = jQuery('input.ssb_porch-railing:checked');
	return toggle.length > 0 && toggle.data('key') === 'no_railing';
};

export const resolveOpeningToFeet = (
	sizeType: string,
	rawSize: number,
	rawOffset: number,
	sideLength,
): { sizeFt: number; offsetFt: number } => {
	if (sizeType === 'percent') {
		return {
			sizeFt: (rawSize / 100) * sideLength,
			offsetFt: (rawOffset / 100) * sideLength,
		};
	}

	// fixed / undefined -> as before: size ft, offset inch/12
	return { sizeFt: rawSize, offsetFt: rawOffset / 12 };
};

/**
 * Show only the presets linked to the given porch style (same pattern as layouts).
 * A preset with no linked style applies to every style, so it is always visible.
 * Hidden presets get `disabled-by-porch-style` so selection logic can skip them.
 */
export const handleShowHidePresetByPorchStyle = (styleID: string) => {
	jQuery(PORCH_PRESET.presetName).each((_, el) => {
		const element = jQuery(el);
		const item = element.closest('.porch-preset-item');
		const styles: string[] = element.data('preset-data')?.porch_styles || [];
		const visible = styles.length === 0 || styles.includes(styleID);
		item.toggleClass('hidden', !visible);
		element.toggleClass('disabled-by-porch-style', !visible);
	});
	// Hide the whole group when no preset is available for this style
	const hasVisible =
		jQuery(`${PORCH_PRESET.presetName}:not(.disabled-by-porch-style)`).length > 0;
	jQuery('.group-upgrade-porch-preset').toggleClass('hidden', !hasVisible);
};
